import React, { FC, useEffect } from 'react';

import { Box, HStack, Stack } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { customTheme } from 'theme';

import { useFlags } from 'app/flagsmith';
import { featureFlags, isFeatureEnabled } from 'app/flagsmith/features';
import {
  DetailsIdentifier,
  StackedHeader,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { IconTypes } from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { CVEAdditionalDetails } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVEAdditionalDetails';
import { CVECommonFields } from 'containers/ActiveCloudResource/Components/ResourceMetadataDrawer/ResourceMetadata/Vulnerability/CVE/CVECommonFields';
import { selectActiveResource } from 'containers/ActiveCloudResource/selectors';
import { actions } from 'containers/ActiveCloudResource/slice';
import { toTitleCase } from 'utils/string';

interface Props {
  currentTechInstance: Record<string, any>;
  isLoading?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export const CVEDetails: FC<Props> = props => {
  const { currentTechInstance: data, onClose, isLoading, isOpen } = props;
  const dispatch = useDispatch();
  const { cveHosts, cveAdditionalDetails } = useSelector(selectActiveResource);
  const { new_vuln_dashboard: vulnerabilityV3Flag } = useFlags([
    featureFlags.vulnerabilityV3,
  ]);
  const useScanVaultVulnerabilityFindings = isFeatureEnabled(
    vulnerabilityV3Flag,
  );

  useEffect(() => {
    if (isEmpty(data)) return;
    dispatch(
      actions.getCVEHosts({
        q: { page: 1, size: 1, cveId: data?.vulnerability_id ?? data?.cve_id },
      }),
    );
    dispatch(
      actions.getCVEAdditionalDetails({
        q: {
          page: 1,
          pageSize: 1,
          cVEByIDRequest: {
            vulnerability_ids: [data?.vulnerability_id ?? data?.cve_id],
          },
          useScanVaultVulnerabilityFindings,
        },
      }),
    );
  }, [data, dispatch, useScanVaultVulnerabilityFindings]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      header={
        <StackedHeader
          upper={data?.vulnerability_id ?? data?.cve_id}
          lower={
            (data?.cvss_score || data?.cvss_v3?.score
              ? 'CVSS V3 Score: ' +
                Number(data?.cvss_score || data?.cvss_v3?.score).toFixed(1) +
                ' ,'
              : '') +
            'Severity: ' +
            toTitleCase(data?.severity)
          }
          type={IconTypes.Vulnerabilities}
          useCustomColor
          color={customTheme.colors?.[data?.severity?.toLowerCase()]}
          isLoading={isLoading}
        />
      }
      body={
        isLoading ? (
          <CustomSpinner size="lg" />
        ) : (
          <Stack spacing={6} flex={1} h="full" overflowY="auto">
            <CVECommonFields row={data} />
            <Box>
              <DetailsIdentifier
                label="Insights"
                value={
                  <HStack spacing={10}>
                    <Box>
                      <WithResourceIcon
                        resourceType={IconTypes.Exploitable}
                        color={
                          !!data?.exploitable
                            ? customTheme.colors.critical
                            : customTheme.colors.green['300']
                        }
                      >
                        {!!data?.exploitable ? 'Has' : 'No'} Public Exploit
                      </WithResourceIcon>
                    </Box>
                    <Box>
                      <WithResourceIcon
                        resourceType={IconTypes.Patch}
                        color={
                          !!data?.fixed_in_version ||
                          !isEmpty(data?.patch_versions)
                            ? customTheme.colors.green['300']
                            : customTheme.colors.critical
                        }
                        useCustomColor
                      >
                        {!!data?.fixed_in_version ||
                        !isEmpty(data?.patch_versions)
                          ? 'Has'
                          : 'No'}{' '}
                        Patch
                      </WithResourceIcon>
                    </Box>
                    <Box>
                      <WithResourceIcon resourceType={IconTypes.List}>
                        {cveHosts.isLoading ? (
                          <CustomSpinner />
                        ) : (
                          cveHosts.data +
                          ' resource' +
                          (cveHosts.data === 1 ? '' : 's')
                        )}
                      </WithResourceIcon>
                    </Box>
                  </HStack>
                }
                isInline={false}
                spacing={2}
                styles={{ label: { fontWeight: 600 } }}
              />
            </Box>
            <CVEAdditionalDetails
              data={
                isEmpty(cveAdditionalDetails.data)
                  ? data
                  : { ...data, ...cveAdditionalDetails.data }
              }
            />
          </Stack>
        )
      }
      styles={{ content: { maxW: '1100px' } }}
      closeButton
    />
  );
};
